import makeErrorFromUnknown from '@/functions/makeErrorFromUnknown';
import { _clientNow } from '@/functions/timezone';
import config from '@/modules/config/functions/config';
import type { BaseError } from '@/modules/errors/constants/error-class-map.gen';
import { isNil } from 'lodash-es';

/**
 * The response from the token endpoint
 * Even though BE response includes expiresIn, we can ignore it and use the expiresAt from the JWT token itself
 * for accurate expiration tracking
 */
export interface TokenResponse {
  accessToken: string;
  accessTokenExpiresIn: number;
  refreshToken?: string;
  refreshTokenExpiresIn?: number;
}

export default function processTokenResponse({
  response,
  InvalidAccessTokenErrorClass,
  InvalidRefreshTokenErrorClass,
}: {
  response: TokenResponse;
  InvalidAccessTokenErrorClass: new (error?: Error) => BaseError;
  InvalidRefreshTokenErrorClass?: new (error?: Error) => BaseError;
}) {
  const currentTime = _clientNow();

  let accessTokenExpiresAt: number | undefined;
  try {
    accessTokenExpiresAt = currentTime
      .add(response.accessTokenExpiresIn, 'seconds')
      .unix();
  } catch (error) {
    throw new InvalidAccessTokenErrorClass(makeErrorFromUnknown(error));
  }
  if (accessTokenExpiresAt === undefined)
    throw new InvalidAccessTokenErrorClass(
      new Error('Access token does not have an expiration date')
    );

  let refreshToken: string | undefined;
  let refreshTokenExpiresAt: number | undefined;

  if (
    config('features.auth.enableTokenRefreshing') === true &&
    !isNil(response.refreshToken) &&
    !isNil(response.refreshTokenExpiresIn) &&
    InvalidRefreshTokenErrorClass !== undefined
  ) {
    try {
      refreshTokenExpiresAt = currentTime
        .add(response.refreshTokenExpiresIn, 'seconds')
        .unix();
    } catch (error) {
      throw new InvalidRefreshTokenErrorClass(makeErrorFromUnknown(error));
    }
    if (refreshTokenExpiresAt === undefined)
      throw new InvalidRefreshTokenErrorClass(
        new Error('Refresh token does not have an expiration date')
      );
    refreshToken = response.refreshToken;
  }

  return {
    accessToken: response.accessToken,
    accessTokenExpiresAt,
    refreshToken,
    refreshTokenExpiresAt,
  };
}
